// components/EventGroupsEditor.js
// Admin editor for event groups (create + edit event).
// Each group: name, courts (picked from the event's courts), capacity and an
// optional price that overrides the base price. The event's max participants
// becomes the sum of the group capacities.
import React from 'react';
import { defaultGroups, newGroupId, totalGroupCapacity, groupHasPrice } from '../utils/eventGroups';

export default function EventGroupsEditor({
  groups,
  onChange,
  courts = [],
  basePrice = 0,
  isFreeEvent = false,
  maxParticipants = 16,
  counts = null, // event.groupCounts when editing an existing event
}) {
  const enabled = Array.isArray(groups) && groups.length > 0;
  const total = totalGroupCapacity(groups);

  const update = (i, patch) => onChange(groups.map((g, k) => (k === i ? { ...g, ...patch } : g)));
  const toggleCourt = (i, court) => {
    const cur = groups[i].courts || [];
    update(i, { courts: cur.includes(court) ? cur.filter((c) => c !== court) : [...cur, court] });
  };
  const usedIn = (court, i) => groups.some((g, k) => k !== i && (g.courts || []).includes(court));
  const inUse = (g) => {
    const c = counts && counts[g.id];
    return !!c && ((c.taken || 0) + (c.reserved || 0) + (c.waiting || 0)) > 0;
  };

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-violet-100 rounded-lg flex-shrink-0">
            <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">分组 Groups</h3>
            <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
              按水平分组（如初级组 / 高水平组），每组有自己的场地、人数和价格；球员报名时选择分组，每组独立候补。
              <br />Split the session into groups — each has its own courts, spots and price. Players pick a group; each group has its own waitlist.
            </p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => onChange(enabled ? [] : defaultGroups(maxParticipants))}
          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-violet-600' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      {enabled && (
        <>
          <div className="space-y-3">
            {groups.map((g, i) => (
              <div key={g.id} className="bg-white rounded-lg border border-violet-200 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <input
                    type="text"
                    value={g.name}
                    maxLength={40}
                    onChange={(e) => update(i, { name: e.target.value })}
                    placeholder="组名 Group name"
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                  />
                  <button
                    type="button"
                    disabled={inUse(g)}
                    title={inUse(g) ? '已有人报名，不能删除 / Has registrations' : '删除 / Remove'}
                    onClick={() => onChange(groups.filter((_, k) => k !== i))}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:text-gray-300 disabled:hover:bg-transparent"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 mb-1">人数 Spots</span>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={g.capacity}
                      onChange={(e) => update(i, { capacity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                    />
                    {counts && counts[g.id] && (
                      <span className="block text-[11px] text-gray-500 mt-1">已报 {(counts[g.id].taken || 0) + (counts[g.id].reserved || 0)} · 候补 {counts[g.id].waiting || 0}</span>
                    )}
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 mb-1">价格 Price ($)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={isFreeEvent}
                      value={groupHasPrice(g) ? g.price : ''}
                      onChange={(e) => update(i, { price: e.target.value === '' ? null : e.target.value })}
                      placeholder={isFreeEvent ? '免费 Free' : `同基础价 $${Number(basePrice || 0)}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none disabled:bg-gray-100"
                    />
                  </label>
                </div>

                <div>
                  <span className="block text-xs font-medium text-gray-600 mb-1">场地 Courts</span>
                  {courts.length === 0 ? (
                    <p className="text-xs text-gray-400">先选择场馆和场地 / Pick the venue and courts first</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {courts.map((court) => {
                        const on = (g.courts || []).includes(court);
                        const taken = !on && usedIn(court, i);
                        return (
                          <button
                            key={court}
                            type="button"
                            onClick={() => toggleCourt(i, court)}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                              on ? 'bg-violet-600 border-violet-600 text-white'
                                : taken ? 'bg-gray-50 border-gray-200 text-gray-400'
                                  : 'bg-white border-gray-300 text-gray-700 hover:border-violet-400'
                            }`}
                            title={taken ? '已分配给其他组 / Used by another group' : ''}
                          >
                            {court}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => onChange([...groups, { id: newGroupId(), name: '', courts: [], capacity: 8, price: null }])}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-violet-400 text-violet-700 text-sm font-semibold hover:bg-violet-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              添加分组 Add group
            </button>
            <span className="text-xs text-gray-600">
              总人数 Total: <b className="text-violet-700">{total}</b>（自动作为人数上限 / used as max participants）
            </span>
          </div>
        </>
      )}
    </div>
  );
}